import TomSelect from "tom-select";

function isTomSelectElement(input) {
  return !!input && typeof input.tagName === 'string' && typeof input.value === 'string';
}

function hasTomSelectValue(value) {
  return Array.isArray(value) ? value.length > 0 : value != null && value !== '';
}

function firstTomSelectValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

function selectedTomSelectText(tomSelect) {
  const value = firstTomSelectValue(tomSelect.getValue());
  if (!hasTomSelectValue(value)) return '';

  const option = tomSelect.options?.[value];
  const labelField = tomSelect.settings?.labelField;
  const label = labelField ? option?.[labelField] : null;
  return typeof label === 'string' && label.length > 0 ? label : String(value);
}

function clearStaleTomSelectSelection(tomSelect, query) {
  const typedText = query?.trim();
  if (!typedText) return;

  const selectedText = selectedTomSelectText(tomSelect).trim();
  if (!selectedText || selectedText === typedText) return;

  tomSelect.clear(true);
  tomSelect.setTextboxValue(query);
  tomSelect.refreshOptions(false);
}

function syncCommittedTomSelectSelection(input, tomSelect) {
  const value = firstTomSelectValue(tomSelect.getValue());
  const selectedText = selectedTomSelectText(tomSelect).trim();

  if (hasTomSelectValue(value) && selectedText) {
    input.dataset.tomSelectCommittedValue = String(value);
    input.dataset.tomSelectCurrentQuery = selectedText;
    input.value = String(value);
  } else {
    delete input.dataset.tomSelectCommittedValue;
  }
}

function syncPendingTomSelectText(input) {
  const tomSelect = input?.tomselect;
  if (!tomSelect) return;

  const pendingText = tomSelect.control_input?.value?.trim();
  const storedQuery = input.dataset.tomSelectCurrentQuery?.trim();
  const committedValue = input.dataset.tomSelectCommittedValue?.trim();
  const selectedValue = tomSelect.getValue();

  if (pendingText) {
    input.value = pendingText;
    return;
  }

  if (committedValue) {
    input.value = committedValue;
    return;
  }

  if (storedQuery) {
    input.value = storedQuery;
    return;
  }

  if (!hasTomSelectValue(selectedValue)) {
    if (!input.value?.trim()) input.value = '';
  }
}

function finalizeSingleTomSelectSelection(tomSelect, value) {
  if (!hasTomSelectValue(value) || !tomSelect) return;

  requestAnimationFrame(() => {
    tomSelect.close(false);
    tomSelect.blur();
  });
}

function disableTomSelectRemoteQueryCache(tomSelect) {
  if (!tomSelect?.settings?.load) return;

  tomSelect.canLoad = function() {
    return true;
  };
}

function bindTomSelectSubmitSync(input) {
  const form = input.closest('form');
  if (!form || input.dataset.tomSelectSubmitSyncBound === 'true') return;

  form.addEventListener('submit', () => {
    syncPendingTomSelectText(input);
  }, { capture: true });

  input.dataset.tomSelectSubmitSyncBound = 'true';
}

function submitTomSelectCurrentText(input) {
  console.log('[autocomplete-submit-current] submitTomSelectCurrentText:start', {
    name: input?.name,
    value: input?.value,
    controlValue: input?.tomselect?.control_input?.value,
  });
  syncPendingTomSelectText(input);
  console.log('[autocomplete-submit-current] submitTomSelectCurrentText:after-sync', {
    name: input?.name,
    value: input?.value,
    controlValue: input?.tomselect?.control_input?.value,
  });

  const form = input.closest('form');
  if (!form) return;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  } else {
    form.submit();
  }
}

function bindTomSelectEnterSubmit(input) {
  const tomSelect = input?.tomselect;
  const controlInput = tomSelect?.control_input;
  if (!controlInput || input.dataset.tomSelectEnterSubmitBound === 'true') return;

  // `/q` uses TomSelect on a plain text search field. TomSelect can keep the
  // hidden input value, live textbox text, and selected option out of sync,
  // especially for prepopulated values and fast Enter submits. We track the
  // in-progress query separately and intercept Enter in capture phase so typed
  // text wins when nothing was selected, while committed selections still win
  // on normal submit.
  console.log('[autocomplete-submit-current] bindTomSelectEnterSubmit', {
    name: input?.name,
    initialValue: input?.value,
    controlValue: controlInput?.value,
  });

  const updateCurrentQuery = source => {
    input.dataset.tomSelectCurrentQuery = controlInput.value || '';
    if (input.dataset.tomSelectCommittedValue && input.dataset.tomSelectCommittedValue !== input.dataset.tomSelectCurrentQuery) {
      delete input.dataset.tomSelectCommittedValue;
    }
    console.log('[autocomplete-submit-current] query-update', {
      source,
      name: input?.name,
      inputValue: input?.value,
      controlValue: controlInput?.value,
      storedQuery: input.dataset.tomSelectCurrentQuery,
      committedValue: input.dataset.tomSelectCommittedValue,
    });
  };

  controlInput.addEventListener('input', () => {
    updateCurrentQuery('input');
  });

  controlInput.addEventListener('keyup', event => {
    if (event.key === 'Enter') return;
    updateCurrentQuery(`keyup:${event.key}`);
  });

  controlInput.addEventListener('keydown', event => {
    console.log('[autocomplete-submit-current] keydown', {
      key: event.key,
      name: input?.name,
      inputValue: input?.value,
      controlValue: controlInput?.value,
      storedQuery: input.dataset.tomSelectCurrentQuery,
      selectedText: selectedTomSelectText(tomSelect),
      committedValue: input.dataset.tomSelectCommittedValue,
    });

    if (event.key !== 'Enter' || event.isComposing) return;

    const typedText = (controlInput.value || input.dataset.tomSelectCurrentQuery || '').trim();
    const selectedText = selectedTomSelectText(tomSelect).trim();

    console.log('[autocomplete-submit-current] enter-eval', {
      typedText,
      selectedText,
      shouldIntercept: !!typedText && typedText !== selectedText,
    });

    if (!typedText || typedText === selectedText) return;

    event.preventDefault();
    event.stopPropagation();
    console.log('[autocomplete-submit-current] enter-intercepted', {
      typedText,
      selectedText,
    });
    input.value = typedText;
    tomSelect.close();
    submitTomSelectCurrentText(input);
  }, true);

  input.dataset.tomSelectEnterSubmitBound = 'true';
}

function renderCardItem(item, escape) {
  let left = `<div class="search-card-name">${escape(item.name)}`;
  if (item.card_num) left += `<span class="card-num">#${escape(String(item.card_num))}</span>`;
  let hasFoilTag = false;
  if (item.tags?.length) {
    item.tags.forEach(tag => {
      if (tag.toLowerCase().includes('foil')) hasFoilTag = true;
      left += `<span class="badge badge-variation">${escape(tag)}</span>`;
    });
  }
  if (!hasFoilTag) {
    if (item.finish === 'foil') left += `<span class="badge badge-variation">Foil</span>`;
    else if (item.finish === 'foil_etched') left += `<span class="badge badge-variation">Foil Etched</span>`;
  }
  left += `</div>`;
  const setCode = (item.set_code || '').toLowerCase();
  const right = `<span class="search-set-name"><i class="ss ss-${escape(setCode)}" title="${escape(item.set || '')}"></i> ${escape(item.set_code || '')}</span>`;
  return `<div class="autocomplete-card-option">${left}${right}</div>`;
}

// Autocomplete for /autocomplete — rich card objects {name, id, set, tags, finish, card_num}
export function initCardIdAutocomplete(input, { submitOnSelect = false } = {}) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();
  const tomSelect = new TomSelect(input, {
    valueField: 'id',
    labelField: 'name',
    searchField: ['name'],
    maxItems: 1,
    create: false,
    dropdownParent: 'body',
    shouldLoad: query => query.length >= 1,
    load(query, callback) {
      fetch(`/autocomplete?term=${encodeURIComponent(query)}`)
        .then(r => r.json())
        .then(callback)
        .catch(() => callback());
    },
    render: {
      option: renderCardItem,
      item: renderCardItem,
    },
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
      if (submitOnSelect && value) {
        input.closest('form')?.submit();
      }
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  return tomSelect;
}

// Autocomplete for /autocomplete_card — simple string array of display names
export function initSimpleCardAutocomplete(input) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();
  const tomSelect = new TomSelect(input, {
    valueField: 'value',
    labelField: 'label',
    searchField: ['label'],
    maxItems: 1,
    create: false,
    shouldLoad: query => query.length >= 1,
    load(query, callback) {
      fetch(`/autocomplete_card?term=${encodeURIComponent(query)}`)
        .then(r => r.json())
        .then(items => callback(items.map(s => ({ value: s, label: s }))))
        .catch(() => callback());
    },
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  if (input.classList.contains('autocomplete-submit-current')) {
    bindTomSelectEnterSubmit(input);
  }
  return tomSelect;
}

// --- globals for legacy view callers ---

window.initCardIdAutocomplete = initCardIdAutocomplete;

export function bindAutoCompleteCard() {
  document.querySelectorAll('input.autocomplete-card, textarea.autocomplete-card, select.autocomplete-card').forEach(initSimpleCardAutocomplete);
}
window.bindAutoCompleteCard = bindAutoCompleteCard;

export function bindSearchAutoComplete(elements, _searchBox, _searchButton, submit) {
  if (!elements) return;
  Array.from(elements).forEach(el => initCardIdAutocomplete(el, { submitOnSelect: !!submit }));
}
window.bindSearchAutoComplete = bindSearchAutoComplete;

function bindCardAutoComplete() {
  document.querySelectorAll('input.autocomplete-card-id, textarea.autocomplete-card-id, select.autocomplete-card-id').forEach(el =>
    initCardIdAutocomplete(el)
  );
  document.querySelectorAll('input.autocomplete-card-id-submit, textarea.autocomplete-card-id-submit, select.autocomplete-card-id-submit').forEach(el =>
    initCardIdAutocomplete(el, { submitOnSelect: true })
  );
}
window.bindCardAutoComplete = bindCardAutoComplete;

document.addEventListener('DOMContentLoaded', () => {
  bindCardAutoComplete();
  bindAutoCompleteCard();

  const loginBtn = document.getElementById('layout-login-signup-btn');
  if (loginBtn) {
    loginBtn.addEventListener('click', () => {
      document.getElementById('layout-login-signup-form')?.submit();
    });
  }
});
